import React, { useCallback, useEffect, useMemo, useState } from 'react'
import B2BLayout from '../../layouts/b2b'
import { Button, Popconfirm, Result, Select, Skeleton, Table, Typography } from 'antd'
import {
  DeleteOutlined,
  EditOutlined,
  FileExcelOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  MessageOutlined,
  PlusOutlined,
  SettingOutlined,
} from '@ant-design/icons'
import fetchAPI, { IHttpError } from '../../../lib/utils/fetch-api'
import { ButtonActionWrapper } from '../../components/list-operations/styles'
import styled from 'styled-components'
import AddModal from './modal-add'
import { entityList } from './entity-list'
import { IOfficesList, ITemplateData } from './interfaces'
import { createSelector } from 'reselect'
import { State } from '../../interfaces'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { objectToUrl } from '../utils'
import { ResultStatusType } from 'antd/es/result'
import dayjs from 'dayjs'

const MapTitle = styled.h3`
  font-size: 18px;
  font-weight: bold;
  padding-bottom: 12px;
  span {
    color: #c5bfbf;
    margin-left: 5px;
  }
`

const createSession = createSelector(
  (state: State) => state.session,
  sessionData => sessionData,
)

interface IError {
  code?: ResultStatusType
  message?: string
}

const DocumentTemplates = () => {
  /**
   * Хлебные крошки
   */
  const breadcrumbs = useMemo(
    () => [
      {
        href: '/admin',
        title: 'Админка',
      },
    ],
    [],
  )

  const { sessionData } = useSelector(createSession)
  const isGoLeader = sessionData?.roles.includes(16)
  const canEditAndDelete = sessionData?.roles.includes(16) || sessionData?.roles.includes(25)

  const [officesList, setOfficesList] = useState<IOfficesList>({
    hr: [],
    sale: [],
  })

  const [urlParams] = useState(Object.fromEntries(new URLSearchParams(location.search)))
  const [currentFilters, setCurrentFilters] = useState<any>({
    office_id: Number(urlParams?.office_id) || null,
  })

  const [data, setData] = useState<any>([])
  const [loading, setLoading] = useState(true)
  const [modalIsOpen, setModalIsOpen] = useState(false)
  const [editingRecord, setEditingRecord] = useState<null | ITemplateData>(null)
  const [error, setError] = useState<null | IError>(null)

  const getOffices = async () => {
    const [hr, sale] = await Promise.all([
      fetchAPI('/api/get-offices?type=Дилер HR').then(response =>
        response.map(item => ({
          value: item.id,
          label: item.name,
        })),
      ),
      fetchAPI('/api/get-offices?type=Дилер').then(response =>
        response.map(item => ({
          value: item.id,
          label: item.name,
        })),
      ),
    ])
    setOfficesList({ hr, sale })
  }

  useEffect(() => {
    getData()
    if (!error && isGoLeader) {
      getOffices()
    }
  }, [])

  /**
   * Получает записи
   */
  const getData = useCallback(async () => {
    setLoading(true)
    const urlParams = Object.fromEntries(new URLSearchParams(location.search))
    await fetchAPI(`/api/templates${objectToUrl(urlParams)}`)
      .then(data => {
        setData(data)
        setLoading(false)
      })
      .catch(e => {
        setError({
          message: (e as IHttpError)?.message || 'Неизвестная ошибка',
          code: ((e as IHttpError)?.statusCode || 'error') as ResultStatusType,
        })
      })
      .finally(() => setLoading(false))
  }, [setData, setLoading])

  /**
   * Удаляет запись
   * @param id
   */
  const remove = (id: number) => {
    fetchAPI(`/api/template/${id}`, { method: 'DELETE' })
      .then(() => {
        getData()
      })
      .catch(e => {
        console.error({ e })
      })
  }

  const editRecord = useCallback(
    (record: any) => {
      setEditingRecord(record)
      setModalIsOpen(true)
    },
    [setEditingRecord, setModalIsOpen],
  )

  const handleModalClose = useCallback(() => {
    setEditingRecord(null)
    setModalIsOpen(false)
  }, [setEditingRecord, setModalIsOpen])

  const columns = [
    {
      title: 'Название',
      dataIndex: 'name',
      width: 190,
      render: (name: string, record: ITemplateData) => {
        const fontSize = '22px'
        const icons = {
          doc: <FileWordOutlined style={{ color: '#1677ff', fontSize }} />,
          xls: <FileExcelOutlined style={{ color: '#52c41a', fontSize }} />,
          pdf: <FilePdfOutlined style={{ color: '#ff4d4f', fontSize }} />,
          message: <MessageOutlined style={{ color: '#722ed1', fontSize }} />,
        }

        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {icons?.[record?.type as keyof typeof icons] || (
              <FileOutlined style={{ color: '#8c8c8c', fontSize }} />
            )}
            <span>{name}</span>
          </span>
        )
      },
    },
    {
      title: 'Описание',
      dataIndex: 'description',
      width: 190,
    },
    {
      title: 'Расположение',
      dataIndex: 'entity',
      width: 190,
      render: (name: undefined | string) => name && entityList?.[name]?.label,
    },
    {
      title: 'Дата редактирования',
      dataIndex: 'updated_at',
      width: 190,
      render: key => dayjs(key).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: 'Действия',
      dataIndex: 'operation',
      width: 110,
      style: '',
      render: (_: any, record) => {
        let configButton = <></>
        let deleteButton = <></>
        const editorButton = ['doc', 'xls'].includes(record?.type) ? (
          <Typography.Link href={`/document_editor/${record.id}`}>
            <ButtonActionWrapper title="Редактор">
              <EditOutlined />
            </ButtonActionWrapper>
          </Typography.Link>
        ) : null
        if (canEditAndDelete) {
          configButton = (
            <Typography.Link>
              <ButtonActionWrapper title="Редактировать" onClick={() => editRecord(record)}>
                <SettingOutlined />
              </ButtonActionWrapper>
            </Typography.Link>
          )
          deleteButton = (
            <Typography.Link>
              <ButtonActionWrapper title="Удалить">
                <Popconfirm
                  title={
                    <>
                      <div>Файл будет удален безвозвратно!</div>Удалить файл?
                    </>
                  }
                  onConfirm={() => remove(record.id)}
                >
                  <DeleteOutlined style={{ color: 'red' }} />
                </Popconfirm>
              </ButtonActionWrapper>
            </Typography.Link>
          )
        }
        return (
          <span
            style={{ display: 'flex', justifyContent: 'center', gap: 10 }}
            onClick={e => {
              e.stopPropagation()
            }}
          >
            {editorButton}
            {configButton}
            {record?.url ? (
              <Typography.Link href={`/api/template/${record.id}`}>
                <ButtonActionWrapper title="Скачать исходный документ">
                  <FileOutlined />
                </ButtonActionWrapper>
              </Typography.Link>
            ) : null}

            {deleteButton}
          </span>
        )
      },
    },
  ]

  if (isGoLeader) {
    columns.unshift({
      title: 'Офис',
      dataIndex: 'office_id',
      width: 190,
      style: '',
      render: (office_id: number[] | undefined) => {
        if (!office_id) return <span />
        const allOffices = [...officesList.hr, ...officesList.sale]
        const ids = Array.isArray(office_id) ? office_id : [office_id]
        return (
          <span>
            {ids.map(id => allOffices.find(o => o.value === Number(id))?.label || id).join(', ')}
          </span>
        )
      },
    })
  }

  const mergedColumns = columns.map(col => {
    return {
      ...col,
      onCell: () => ({}),
    }
  })

  const handleAdd = useCallback(() => {
    getData()
    setModalIsOpen(false)
  }, [getData, setModalIsOpen])

  const navigate = useNavigate()
  /**
   * Отслеживает состояние фильтров
   * @param filters
   */
  const changeFiltersHandler = useCallback(
    filters => {
      const clear = Object.keys(filters).reduce((acc, val) => {
        if (filters[val]) {
          acc[val] = filters[val]
        }
        return acc
      }, {})

      setCurrentFilters(clear)
      const newUrlParams = new URLSearchParams(clear).toString()
      navigate({ search: newUrlParams })
      getData()
    },
    [setCurrentFilters],
  )

  const fullOfficesList = useMemo(() => {
    if (!officesList) {
      return []
    }
    return [...officesList.hr, ...officesList.sale].sort((a, b) => a.value - b.value)
  }, [officesList])

  return (
    <B2BLayout breadcrumbs={breadcrumbs} isViewScrollButton>
      {loading && <Skeleton active />}
      {!loading && error && <Result status={error.code} title={error.message} />}
      {!loading && !error && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <MapTitle>Шаблоны документов</MapTitle>
            <Button
              onClick={() => {
                setEditingRecord(null)
                setModalIsOpen(true)
              }}
            >
              <PlusOutlined /> Добавить шаблон
            </Button>
            <AddModal
              onOk={handleAdd}
              onCancel={() => handleModalClose()}
              modalIsOpen={modalIsOpen}
              record={editingRecord}
              officesList={officesList}
            />
          </div>
          {fullOfficesList?.length > 1 ? (
            <div style={{ display: 'flex', gap: 4, paddingBottom: 10 }}>
              <Select
                placeholder="Офис"
                showSearch={true}
                value={currentFilters?.office_id}
                style={{ width: 200 }}
                onChange={val => changeFiltersHandler({ ...currentFilters, office_id: val })}
                allowClear
                onClear={() => setCurrentFilters(cur => ({ ...cur, office_id: undefined }))}
                options={fullOfficesList}
              />
            </div>
          ) : null}
          <Table
            rowKey="id"
            loading={loading}
            size="small"
            scroll={{ x: 'max-content' }}
            dataSource={data}
            columns={mergedColumns}
            rowClassName={'editable-row'}
          />
        </>
      )}
    </B2BLayout>
  )
}

export default DocumentTemplates
